import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, NavLink, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import AnchorScroll from './components/AnchorScroll'
import { AuthProvider, IsDeveloper, IsExecutive, IsMarketing, IsStaff, IsTeamToolsUser, RequireAuth } from './components/Auth'
import { CsrfProvider } from './components/Csrf'
import { Menu } from './components/Menu'
import NotFound from './components/NotFound'
import { PageContextProvider } from './components/Page'
import { PageFooter } from './components/PageFooter'
import ScrollToTop from './components/ScrollToTop'
import Spinner from './components/Spinner'
import { AuthRoutes, MfaPage } from './features/auth'
import { RequireMfa } from './features/auth/components'
import { BankRoutes } from './features/bank'
import { KillRaceRoutes } from './features/career/routes'
import { DownloadRoutes } from './features/download'
import { LootboxRoutes } from './features/lootbox/routes'
import { PacksRoutes } from './features/packs/routes'
import { TradeRoutes } from './features/trade/routes'
import { StakeRoutes } from './features/stake/routes'
import { ModalProvider } from './hooks/useModal'
import './i18n'
import { LandingPage } from './routes'
import { BankDeposit } from './team_tools/bank/deposit'
import { ServerControl } from './team_tools/dashboard/server_control'
import { Statistic } from './team_tools/dashboard/statistic'
import { TeamMembers } from './team_tools/dashboard/team_members'
import { FeatureEnabled, FeatureOff } from './team_tools/components/FeatureFlags'
import { Allowed, ROLES } from './team_tools/components/Capabilities'
import { AccountsHome, TeamToolsHome } from './team_tools/components/Home'
import { StoreRoutes } from './features/store/routes'
import { Banner, BannerProvider, useBanner } from './components/banner'
import { useUser } from './features/auth/api/useUser'
import { MarketplaceRoutes } from './features/marketplace/routes'
import { AffiliateRoutes } from './features/affiliate/routes'

const Account = lazy(() => import('./routes/account'))
// const DataTools = lazy(() => import('./routes/data-tools'))
// const DataToolsIndex = lazy(() => import('./routes/data-tools/index'))
// const DataToolsHolders = lazy(() => import('./routes/data-tools/holders'))
// const DataToolsServers = lazy(() => import('./routes/data-tools/servers'))
// const DataToolsPlayers = lazy(() =>
//     import('./routes/data-tools/players').then((module) => ({ default: module['Players'] }))
// )
const Dashboard = lazy(() => import('./routes/dashboard').then((module) => ({ default: module['Dashboard'] })))
// TeanTools
const TeamTools = lazy(() => import('./team_tools'))
const TeamToolsDashboard = lazy(() =>
    import('./team_tools/dashboard').then((module) => ({ default: module['TeamToolsDashboard'] }))
)
const ApiInspector = lazy(() =>
    import('./team_tools/api_inspector').then((module) => ({ default: module['ApiInspector'] }))
)
const BanList = lazy(() => import('./team_tools/ban_list/ban_list'))
const BanListUsers = lazy(() => import('./team_tools/ban_list/users'))
const Docs = lazy(() => import('./team_tools/docs').then((module) => ({ default: module['DocsPage'] })))
// The tab group, not the Auto Mod panel itself. `AntiCheat` is still exported
// and is what the first tab renders; this wrapper adds Cheat reports and Player
// integrity beside it, both moved here from Server Control.
const AntiCheat = lazy(() => import('./team_tools/anticheat').then((module) => ({ default: module['AutoMod'] })))
const Tournaments = lazy(() => import('./team_tools/tournaments'))
// Bank
const Bank = lazy(() => import('./team_tools/bank'))
const BankAccounts = lazy(() => import('./team_tools/bank/accounts'))
const BankBr1 = lazy(() => import('./team_tools/bank/br1'))
const BankAirdrop = lazy(() => import('./team_tools/bank/airdrop').then((module) => ({ default: module['Airdrop'] })))
const DeploymentLogs = lazy(() =>
    import('./team_tools/dashboard/DeploymentLogs').then((module) => ({ default: module['DeploymentLogs'] }))
)
const PacksSection = lazy(() =>
    import('./team_tools/packs').then((module) => ({ default: module['PacksSection'] }))
)
const PacksManage = lazy(() =>
    import('./team_tools/packs/manage').then((module) => ({ default: module['PacksManage'] }))
)
const PacksTester = lazy(() =>
    import('./team_tools/packs/tester').then((module) => ({ default: module['PacksTester'] }))
)
const BankStoreOrders = lazy(() =>
    import('./team_tools/bank/store_orders').then((module) => ({ default: module['StoreOrders'] }))
)
const BankTransactions = lazy(() =>
    import('./team_tools/bank/transactions').then((module) => ({ default: module['Transactions'] }))
)
// Store tools: catalogue, bulk edits and merchandising. See STORE_TOOLS.md.
const Skins = lazy(() => import('./team_tools/skins').then((module) => ({ default: module['Skins'] })))
const StoreTools = lazy(() => import('./team_tools/store'))
const StoreCatalog = lazy(() =>
    import('./team_tools/store/catalog').then((module) => ({ default: module['StoreCatalog'] }))
)
const StoreMerchandising = lazy(() =>
    import('./team_tools/store/merchandising').then((module) => ({
        default: module['StoreMerchandising'],
    }))
)
const StoreOverview = lazy(() =>
    import('./team_tools/store/overview').then((module) => ({ default: module['StoreOverview'] }))
)
const StoreOrders = lazy(() =>
    import('./team_tools/store/orders').then((module) => ({ default: module['StoreOrders'] }))
)
//Accounts
const AffiliateAdmin = lazy(() => import('./team_tools/affiliate'))
const AffiliateApplications = lazy(() =>
    import('./team_tools/affiliate/applications').then((m) => ({ default: m['AffiliateApplications'] }))
)
const AffiliateAdminList = lazy(() =>
    import('./team_tools/affiliate/affiliates').then((m) => ({ default: m['AffiliateList'] }))
)
const Accounts = lazy(() => import('./team_tools/accounts'))
const Users = lazy(() => import('./team_tools/accounts/users'))
const User = lazy(() => import('./team_tools/accounts/user'))
const SessionActivity = lazy(() => import('./team_tools/accounts/activity'))

const CollectionInfo = lazy(() => import('./routes/collection-info'))
const Collection = lazy(() => import('./routes/collection'))

// Public, no-backend design harness for the 3D pack visuals. Not linked in the
// nav; remove with its component once the real packs page is back on a DB.
const PackPreview = lazy(() => import('./features/packs/routes/PackPreview'))
const ServerControlPreview = lazy(() => import('./team_tools/dashboard/servers/preview'))
const Community = lazy(() => import('./team_tools/community'))
const TournamentPreview = lazy(() => import('./team_tools/tournaments/preview'))

/**
 * The strip above the menu bar.
 *
 * It used to set the headline in `font-monument` and the sentence after it in the body face, so a
 * single line of promo carried two typefaces at two sizes. One family now, with weight and opacity
 * doing the separating.
 *
 * The whole strip is the link as well. Only the headline was clickable before, which is a small
 * target for the one thing this bar exists to get you to do, and it left the sentence beside it
 * looking inert.
 */
const PromoBanner = ({ to, headline, body, short }: { to: string; headline: string; body: string; short: string }) => (
    <NavLink to={to} className='group flex flex-row items-center justify-center gap-x-2 px-4 py-1 text-center'>
        {/*
          One line on a phone, the full sentence from `sm` up.

          The long copy wrapped to three lines in a strip meant to be a thin ribbon, and the arrow
          came to rest alone on the last of them. `short` carries the offer rather than the
          instruction: on a 375px screen the useful half of "sign up now and claim a $5 USD bonus,
          on us" is the five dollars.
        */}
        <span className='sm:hidden font-black uppercase tracking-[0.12em] whitespace-nowrap'>{short}</span>
        <span className='hidden sm:inline font-black uppercase tracking-[0.15em]'>{headline}</span>
        <span className='hidden sm:inline font-semibold uppercase tracking-wide opacity-80'>{body}</span>
        <FontAwesomeIcon icon={faArrowRight} className='shrink-0 transition-transform group-hover:translate-x-1' />
    </NavLink>
)

const App = () => {
    const { data: user, isFetched } = useUser()
    const { setBanner } = useBanner()

    const { pathname } = useLocation()
    // The promo belongs to the player-facing site. Team Tools is a staff
    // console -- a strip inviting an operator to go shopping sits above the
    // screen they are working in, pushes it down, and is the one thing on the
    // page that is not about the job.
    const isStaffTool =
        pathname.startsWith('/team-tools') ||
        pathname.startsWith('/tournament-preview') ||
        pathname.startsWith('/server-control-preview')

    useEffect(() => {
        if (!isFetched) return
        if (isStaffTool) {
            setBanner(null)
        } else if (user?.is_auth) {
            setBanner(
                <PromoBanner
                    to='/store'
                    headline='Explore the store'
                    body='Exclusive items, at a discount, with the balance you already have.'
                    short='Shop exclusive items'
                />
            )
        } else {
            setBanner(
                <PromoBanner
                    to='/signup'
                    headline='Get started for free'
                    body='Sign up and we will put $5 in your balance to play with.'
                    short='Claim your $5 bonus'
                />
            )
        }
    }, [user, isFetched, isStaffTool])

    return (
        <main className='flex flex-col w-full min-h-screen pt-[var(--page-menu-height)] md:pt-0'>
            <section className='relative flex-1 flex flex-col min-h-screen'>
                <div id='page-background' className='fixed inset-0 w-full max-w-[1920px] mx-auto'></div>
                <PageContextProvider>
                    <ScrollToTop />
                    <Menu />
                    <Banner />
                    <div id='modal-root'></div>
                    <Suspense
                        fallback={
                            <div className='min-h-screen h-full w-full flex flex-row items-center justify-center'>
                                <Spinner />
                            </div>
                        }
                    >
                        <AnchorScroll>
                            <section className='relative flex-1'>
                                <Routes>
                                    <Route path='/'>
                                        <Route index element={<LandingPage />} />
                                        {/*
                                          Declared before the AuthRoutes catch-all so it is not
                                          swallowed by it. AuthRoutes redirects any activated user
                                          to /dashboard, which would make this page unreachable for
                                          exactly the staff who need it.
                                        */}
                                        <Route
                                            path='2fa'
                                            element={
                                                <RequireAuth>
                                                    <MfaPage />
                                                </RequireAuth>
                                            }
                                        />
                                        <Route path='/*' element={<AuthRoutes />} />
                                        <Route
                                            path='dashboard'
                                            element={
                                                <RequireAuth>
                                                    <Dashboard />
                                                </RequireAuth>
                                            }
                                        />
                                        <Route
                                            path='account'
                                            element={
                                                <RequireAuth>
                                                    <Account />
                                                </RequireAuth>
                                            }
                                        />
                                        <Route path='kill-race'>{...KillRaceRoutes()}</Route>
                                        <Route path='renting'>{...StakeRoutes}</Route>
                                        <Route path='loot-boxes'>{...LootboxRoutes}</Route>
                                        {/*
                                          Behind RequireAuth so an anonymous visitor is sent to
                                          log in rather than shown a page whose every action the
                                          API will refuse. The real gate is packs.Config on the
                                          server; this only saves a pointless round trip.
                                        */}
                                        <Route
                                            path='packs'
                                            element={
                                                <RequireAuth>
                                                    <Outlet />
                                                </RequireAuth>
                                            }
                                        >
                                            {...PacksRoutes}
                                        </Route>
                                        {/*
                                          The BR1-native marketplace, settled in BR1 balance.
                                          Distinct from /marketplace, which is the Magic Eden
                                          browser. Staff-only on the server for now, so this
                                          RequireAuth only avoids a round trip an anonymous
                                          visitor would lose anyway.
                                        */}
                                        <Route
                                            path='trade'
                                            element={
                                                <RequireAuth>
                                                    <Outlet />
                                                </RequireAuth>
                                            }
                                        >
                                            {...TradeRoutes}
                                        </Route>
                                        <Route
                                            path='download/*'
                                            element={
                                                <RequireAuth fallback='/signup'>
                                                    <DownloadRoutes />
                                                </RequireAuth>
                                            }
                                        />
                                        <Route path='collection' element={<CollectionInfo />} />
                                        <Route path='collection/:name' element={<Collection />} />
                                        {/* Public design harness — no auth, no API. */}
                                        <Route path='pack-preview' element={<PackPreview />} />
                                        <Route path='server-control-preview' element={<ServerControlPreview />} />
                                        {/* Same idea, for the tournament form. */}
                                        <Route path='tournament-preview' element={<TournamentPreview />} />
                                        <Route path='marketplace'>{...MarketplaceRoutes}</Route>
                                        <Route path='store'>{...StoreRoutes}</Route>
                                        <Route path='affiliate'>{...AffiliateRoutes}</Route>
                                        <Route
                                            path='team-tools'
                                            element={
                                                <RequireAuth>
                                                    {/*
                                                      Any role flag, or a capability granted in the
                                                      admin -- an outside community manager holds
                                                      only the latter. Each section below then gates
                                                      itself on the flags it always needed, or the
                                                      capability that covers it.
                                                    */}
                                                    <IsTeamToolsUser show403={true}>
                                                        {/*
                                                          Staff without a confirmed 2FA device are sent
                                                          to /2fa and cannot proceed until they enrol.
                                                          Inside the gate so only staff are ever checked;
                                                          the server counts a grant as staff for this.
                                                        */}
                                                        <RequireMfa>
                                                            <TeamTools />
                                                        </RequireMfa>
                                                    </IsTeamToolsUser>
                                                </RequireAuth>
                                            }
                                        >
                                            <Route index element={<TeamToolsHome />} />
                                            <Route
                                                path='dashboard'
                                                element={
                                                    <IsStaff show403={true}>
                                                        <TeamToolsDashboard />
                                                    </IsStaff>
                                                }
                                            >
                                                <Route index element={<Statistic />} />
                                                <Route path='team-members' element={<TeamMembers />} />
                                                {/* Server Control's old home. It is a top-level
                                                    tab now, and the route had been left behind
                                                    here when the tab moved -- so the tab pointed
                                                    at /team-tools/server-control, nothing was
                                                    mounted there, and the one console that drains
                                                    servers and kicks players answered 404.

                                                    Kept as a redirect rather than deleted, the
                                                    same way `anticheat` is below: this path is in
                                                    bookmarks and in Discord threads, and a dead
                                                    link is a worse migration than a permanent
                                                    redirect. */}
                                                <Route
                                                    path='server-control'
                                                    element={<Navigate to='/team-tools/server-control' replace />}
                                                />
                                                <Route path='deployment-logs' element={<DeploymentLogs />} />
                                            </Route>
                                            <Route path='server-control' element={<ServerControl />} />
                                            {/* Player and bug reports. Lazy:
                                                a screen most visits never
                                                open. */}
                                            <Route path='community' element={<Community />} />
                                            {/* Remote config is a tab on Game
                                                Control now. Anything bookmarked
                                                while it had its own entry still
                                                lands somewhere useful. */}
                                            <Route
                                                path='remote-config'
                                                element={<Navigate to='/team-tools/server-control' replace />}
                                            />
                                            <Route
                                                path='api-inspector'
                                                element={
                                                    <IsDeveloper show403={true}>
                                                        <ApiInspector />
                                                    </IsDeveloper>
                                                }
                                            />
                                            <Route
                                                path='api-inspector/:name'
                                                element={
                                                    <IsDeveloper show403={true}>
                                                        <ApiInspector />
                                                    </IsDeveloper>
                                                }
                                            />
                                            {/* Packs is a Store tab now. Its old
                                                paths are in bookmarks and in links
                                                other screens render, so they redirect
                                                rather than dying. */}
                                            <Route
                                                path='packs'
                                                element={<Navigate to='/team-tools/store/packs' replace />}
                                            />
                                            <Route
                                                path='packs/:screen'
                                                element={<Navigate to='/team-tools/store/packs' replace />}
                                            />
                                            {/* Kept as a redirect rather than deleted. This
                                                path is in bookmarks, in Discord threads and in
                                                links other screens render -- the integrity
                                                ranking on Server Control still points at it --
                                                and a dead link is a worse migration than a
                                                permanent redirect. */}
                                            <Route
                                                path='anticheat'
                                                element={<Navigate to='/team-tools/accounts/auto-mod' replace />}
                                            />
                                            <Route path='docs'>
                                                <Route path=':page' element={<Docs />} />
                                                <Route index element={<Navigate to='gameapi' replace />} />
                                            </Route>
                                            {/* Closed Beta is a tab under Community Control now.
                                                The old paths stay as redirects rather than
                                                disappearing -- these are bookmarked. */}
                                            <Route
                                                path='closed-beta/*'
                                                element={<Navigate to='/team-tools/community' replace />}
                                            />
                                            <Route
                                                path='bank'
                                                element={
                                                    <IsMarketing show403={true}>
                                                        <Bank />
                                                    </IsMarketing>
                                                }
                                            >
                                                <Route path='accounts' element={<BankAccounts />} />
                                                <Route path='br1' element={<BankBr1 />}>
                                                    {/* Gated as the server gates it. The nav already
                                                        hid the link from non-executives, but a typed URL
                                                        rendered the whole form, and the refusal came back
                                                        as an HTML 500 the form showed as "Error: Error"
                                                        (audit screen check C5). */}
                                                    <Route
                                                        path='deposit'
                                                        element={
                                                            <IsExecutive show403={true}>
                                                                <BankDeposit />
                                                            </IsExecutive>
                                                        }
                                                    />
                                                    <Route path='airdrop' element={<BankAirdrop />} />
                                                </Route>
                                                <Route path='transactions' element={<BankTransactions />} />
                                                <Route path='shop-transactions' element={<BankStoreOrders />} />
                                                <Route path=':user/transactions' element={<BankTransactions />} />
                                                <Route
                                                    path=':user/transactions/:currency'
                                                    element={<BankTransactions />}
                                                />
                                                <Route index element={<Navigate to='br1' replace />} />
                                                {...BankRoutes()}
                                            </Route>
                                            {/*
                                              No role wrapper: the store team is everyone with
                                              Team Tools access, which IsStaff + RequireMfa above
                                              already establishes. The endpoints agree
                                              (StaffPermission) -- see STORE_TOOLS.md §1.
                                            */}
                                            {/*
                                              Gated twice on purpose. IsStaff is who you are;
                                              FeatureEnabled is whether Skins is switched on at
                                              all. The nav already leaves the tab out when it is
                                              off, so reaching here means a bookmark or a typed
                                              URL -- which should say so rather than render an
                                              empty screen that reads as a broken build. The
                                              server refuses every Skins endpoint either way.
                                            */}
                                            <Route
                                                path='skins'
                                                element={
                                                    <IsStaff show403={true}>
                                                        <FeatureEnabled
                                                            name='skins'
                                                            fallback={<FeatureOff title='Skins' />}
                                                        >
                                                            <Skins />
                                                        </FeatureEnabled>
                                                    </IsStaff>
                                                }
                                            />
                                            <Route
                                                path='tournaments'
                                                element={
                                                    <Allowed
                                                        roles={ROLES.marketing}
                                                        capability='manage_tournaments'
                                                        show403={true}
                                                    >
                                                        <Tournaments />
                                                    </Allowed>
                                                }
                                            />
                                            {/* A role flag, not a capability: the store endpoints
                                                are StaffPermission and no grant reaches them. */}
                                            <Route
                                                path='store'
                                                element={
                                                    <IsStaff show403={true}>
                                                        <StoreTools />
                                                    </IsStaff>
                                                }
                                            >
                                                <Route path='catalog' element={<StoreCatalog />} />
                                                <Route path='merchandising' element={<StoreMerchandising />} />
                                                <Route path='orders' element={<StoreOrders />} />
                                                <Route path='overview' element={<StoreOverview />} />
                                                {/* The other way the same weapons reach a
                                                    player, so it sits beside the catalog. */}
                                                <Route path='packs' element={<PacksSection />}>
                                                    <Route path='manage' element={<PacksManage />} />
                                                    <Route path='tester' element={<PacksTester />} />
                                                    <Route index element={<Navigate to='manage' replace />} />
                                                </Route>
                                                <Route index element={<Navigate to='catalog' replace />} />
                                            </Route>
                                            <Route
                                                path='accounts'
                                                element={
                                                    <Allowed
                                                        roles={ROLES.marketing}
                                                        capability={['view_players', 'view_anticheat']}
                                                        show403={true}
                                                    >
                                                        <Accounts />
                                                    </Allowed>
                                                }
                                            >
                                                <Route
                                                    path='users'
                                                    element={
                                                        <Allowed
                                                            roles={ROLES.marketing}
                                                            capability='view_players'
                                                            show403={true}
                                                        >
                                                            <Users />
                                                        </Allowed>
                                                    }
                                                />
                                                <Route
                                                    path='users/:username'
                                                    element={
                                                        <Allowed
                                                            roles={ROLES.marketing}
                                                            capability='view_players'
                                                            show403={true}
                                                        >
                                                            <User />
                                                        </Allowed>
                                                    }
                                                />
                                                {/* Beside the account list because it answers a
                                                    question about accounts that the list cannot:
                                                    the list's session count is lifetime, this one
                                                    is bounded to a day or a range. */}
                                                <Route
                                                    path='activity'
                                                    element={
                                                        <Allowed
                                                            roles={ROLES.marketing}
                                                            capability='view_anticheat'
                                                            show403={true}
                                                        >
                                                            <SessionActivity />
                                                        </Allowed>
                                                    }
                                                />
                                                {/* Auto Mod, as a tab here rather than a
                                                    top-level one. Marketing or the
                                                    view_anticheat grant, the same as the
                                                    Accounts route around it and the tab
                                                    (decision D-078). */}
                                                <Route
                                                    path='auto-mod'
                                                    element={
                                                        <Allowed
                                                            roles={ROLES.marketing}
                                                            capability='view_anticheat'
                                                            show403={true}
                                                        >
                                                            <AntiCheat />
                                                        </Allowed>
                                                    }
                                                />
                                                <Route index element={<AccountsHome />} />
                                            </Route>
                                            {/*
                                              Marketing review the queue and record payouts, the
                                              same people who already handle referrals next door
                                              under Accounts.
                                            */}
                                            <Route
                                                path='affiliate'
                                                element={
                                                    <IsMarketing show403={true}>
                                                        <AffiliateAdmin />
                                                    </IsMarketing>
                                                }
                                            >
                                                <Route path='applications' element={<AffiliateApplications />} />
                                                <Route path='affiliates' element={<AffiliateAdminList />} />
                                                <Route index element={<Navigate to='applications' replace />} />
                                            </Route>
                                            <Route
                                                path='ban-list'
                                                element={
                                                    // D-115: it had no gate and no nav entry;
                                                    // now both match the ban tool endpoints.
                                                    <Allowed roles={ROLES.developer} capability='ban_players' show403={true}>
                                                        <BanList />
                                                    </Allowed>
                                                }
                                            >
                                                <Route path='users' element={<BanListUsers />} />
                                                {/* Without this, /team-tools/ban-list rendered the
                                                    tab strip over an empty Outlet, which reads as
                                                    a screen that failed to load rather than as a
                                                    section index. Every other section here
                                                    redirects to its first tab. */}
                                                <Route index element={<Navigate to='users' replace />} />
                                            </Route>
                                            {/* There is no separate airdrop screen. This path
                                                rendered a placeholder "123" under the title
                                                "Airdrop" (audit BANK-57, C6); the real tool is
                                                under Bank. */}
                                            <Route
                                                path='airdrop'
                                                element={<Navigate to='/team-tools/bank/br1/airdrop' replace />}
                                            />
                                        </Route>
                                    </Route>
                                    <Route path='*' element={<NotFound />} />
                                </Routes>
                            </section>
                        </AnchorScroll>
                        <ToastContainer position='bottom-right' hideProgressBar autoClose={3000} theme='dark' />
                    </Suspense>
                    <div className=' relative z-10 bg-black'>
                        <div className='bg-[linear-gradient(90.01deg,#C91FFF_0%,#03CEFF_100%)] h-4 w-full'>&nbsp;</div>
                        <PageFooter />
                    </div>
                </PageContextProvider>
            </section>
        </main>
    )
}

const root = ReactDOM.createRoot(document.getElementById('app-root') as HTMLElement)

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
})

root.render(
    <QueryClientProvider client={queryClient}>
        <CsrfProvider>
            <AuthProvider>
                <BrowserRouter>
                    <ModalProvider>
                        <BannerProvider>
                            <App />
                        </BannerProvider>
                    </ModalProvider>
                </BrowserRouter>
            </AuthProvider>
        </CsrfProvider>
    </QueryClientProvider>
)
